import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';

// The SDP page keeps its tab and every tab's filters in the query string, so a
// refresh, back/forward or a pasted link lands on the same view. Defaults are
// never written (the bare URL is the default view), and a value the parser
// rejects reads as the default, so a hand-edited URL can't break the page.

const toSearch = (params) => {
  const search = queryString.stringify(params, { skipNull: true, skipEmptyString: true });
  return search ? `?${search}` : '';
};

// Patches the query string: null / undefined / '' removes a key. Reads
// history.location at call time (not the render's snapshot) so two setters
// fired from one handler both land. `push` adds a history entry (tab
// changes); the default replace keeps filter edits out of back/forward.
// `reset` starts from an empty query instead of the current one.
export const useSetQueryParams = () => {
  const history = useHistory();
  return useCallback(
    (patch, { push = false, reset = false } = {}) => {
      const current = reset ? {} : queryString.parse(history.location.search);
      const search = toSearch({ ...current, ...patch });
      if (search === history.location.search) return;
      (push ? history.push : history.replace)({ search });
    },
    [history],
  );
};

// One query param as state. `parse` maps the raw string to a value, or
// undefined when it is invalid (→ defaultValue). Setting the default removes
// the key.
const useQueryState = (key, { defaultValue = '', parse = (raw) => raw, serialize = String } = {}) => {
  const { search } = useLocation();
  const setParams = useSetQueryParams();
  const raw = queryString.parse(search)[key];
  const parsed = typeof raw === 'string' && raw !== '' ? parse(raw) : undefined;
  const value = parsed === undefined ? defaultValue : parsed;

  const setValue = useCallback(
    (next, options) => setParams({ [key]: next === defaultValue || next == null ? null : serialize(next) }, options),
    [setParams, key, defaultValue, serialize],
  );
  return [value, setValue];
};

export default useQueryState;

// Parsers: each returns undefined for anything it does not accept.
export const oneOf = (values) => (raw) => (values.includes(raw) ? raw : undefined);

export const isoDate = (raw) => (moment(raw, 'YYYY-MM-DD', true).isValid() ? raw : undefined);

export const positiveInt = (raw) => (/^[1-9]\d*$/.test(raw) ? Number(raw) : undefined);

export const flag = (raw) => (raw === '1' ? true : undefined);
export const serializeFlag = (value) => (value ? '1' : null);
